import * as THREE from 'three'
import { viewerApi } from '@viewer/lib/viewerApi'
import { collectWorldTriangles, type WorldTrianglesSource } from '@viewer/lib/worldTriangles'
import { materiaisDosElementos } from '@viewer/lib/classColors'
import { namesFor } from '@viewer/clash/clashModels'
import { pointCloudApi } from '../pointCloudApi'
import {
  type ElementoAComparar,
  type Estatistica,
  type LinhaDoRelatorio,
  type PlanoRelativo,
  acumular,
  construirGrelha,
  corDoDesvio,
  dentroDosPlanos,
  maisProximo,
  novaEstatistica,
  planoRelativo,
  pontoMaisProximoDoElemento,
  relatorio,
} from './geometria'
import { type Eixo, type SemEixo, ajustarEixo } from './eixo'
import { classeComparavel, materialCorresponde } from './escolha'

/** A ligação entre o núcleo puro (`geometria.ts`, `eixo.ts`) e o viewer:
 *  ir buscar os triângulos dos elementos escolhidos, os pontos da
 *  nuvem, os planos de corte activos, e guardar o resultado para o
 *  painel. */

export interface OpcoesDaComparacao {
  /** A que distância um ponto ainda conta como sendo de um elemento. */
  raio: number
  tolerancia: number
  /** Só os pontos que o corte deixa ver. Serve para comparar um piso,
   *  ou um alinhamento, sem mexer no resto. */
  respeitarCortes: boolean
}

export interface LinhaComNome extends LinhaDoRelatorio {
  modelId: string
  localId: number
  nome: string | null
  classe: string
  eixo: Eixo | SemEixo | null
}

export interface ResultadoDaComparacao {
  cloudId: string
  modelId: string
  opcoes: OpcoesDaComparacao
  linhas: LinhaComNome[]
  /** Pontos da nuvem vistos, dentro do corte, e atribuídos a algum
   *  elemento. */
  pontosVistos: number
  pontosNoCorte: number
  pontosAtribuidos: number
  /** Pontos que ficaram mais perto de um elemento que não se escolheu
   *  (o pilar de betão ao lado do de aço), e quantos desses havia. */
  pontosDeVizinhos: number
  vizinhos: number
  elementosSemGeometria: number
  truncado: boolean
  /** As cores dos dois modos. As do levantamento são as que a nuvem
   *  trouxe (ou nenhumas); as do desvio são calculadas aqui. */
  coresDoLevantamento: THREE.BufferAttribute | null
  coresDoDesvio: THREE.BufferAttribute
  /** A geometria em que as cores foram calculadas. Se a nuvem for lida
   *  outra vez com outra densidade, deixa de servir. */
  geometria: THREE.BufferGeometry
  modo: 'levantamento' | 'desvio'
}

export interface ProgressoDaComparacao {
  fase: 'elementos' | 'geometria' | 'pontos' | 'eixos'
  feito: number
  total: number
}

const resultados = new Map<string, ResultadoDaComparacao>()
const ouvintes = new Set<() => void>()
let versao = 0

function avisar() {
  versao += 1
  for (const o of ouvintes) o()
}

// Se a nuvem sair, ou for lida outra vez com outra densidade, o
// resultado deixa de corresponder aos pontos que se vêem. Larga-se.
pointCloudApi.subscribe(() => {
  let mudou = false
  const vivas = new Map(pointCloudApi.list().map((e) => [e.id, e]))
  for (const [id, r] of resultados) {
    const e = vivas.get(id)
    if (!e || e.object.geometry !== r.geometria) {
      resultados.delete(id)
      mudou = true
    }
  }
  if (mudou) avisar()
})

/** As classes do modelo com quantos elementos cada uma, só as que se
 *  podem comparar com pontos. */
export async function classesDoModelo(modelId: string): Promise<Map<string, number[]>> {
  const out = new Map<string, number[]>()
  const model = viewerApi.getModel(modelId)
  if (!model) return out
  const comGeometria = new Set(await model.getItemsIdsWithGeometry())
  const baldes = await model.getItemsOfCategories([/.*/])
  for (const [classe, ids] of Object.entries(baldes)) {
    if (!Array.isArray(ids) || !classeComparavel(classe)) continue
    const com = ids.filter((id) => comGeometria.has(id))
    if (com.length > 0) out.set(classe.toUpperCase(), com)
  }
  return out
}

/** Os elementos que entram: das classes pedidas e, se houver padrão de
 *  material, só os que lhe correspondem. */
export async function elementosEscolhidos(
  modelId: string,
  classes: string[],
  material: string,
): Promise<{ ids: number[]; classePorId: Map<number, string>; semMaterial: number }> {
  const porClasse = await classesDoModelo(modelId)
  const classePorId = new Map<number, string>()
  for (const c of classes) for (const id of porClasse.get(c.toUpperCase()) ?? []) classePorId.set(id, c.toUpperCase())
  let ids = [...classePorId.keys()]
  let semMaterial = 0
  if (material.trim()) {
    const model = viewerApi.getModel(modelId)
    const materiais = model ? await materiaisDosElementos(model, ids) : new Map<number, string>()
    semMaterial = ids.filter((id) => !materiais.has(id)).length
    ids = ids.filter((id) => materialCorresponde(materiais.get(id), material))
  }
  return { ids, classePorId, semMaterial }
}

/** A classe de cada elemento pedido, para os que vêm da selecção. */
async function classesDe(modelId: string, ids: number[]): Promise<Map<number, string>> {
  const porClasse = await classesDoModelo(modelId)
  const pedido = new Set(ids)
  const out = new Map<number, string>()
  for (const [c, lista] of porClasse) for (const id of lista) if (pedido.has(id)) out.set(id, c)
  return out
}

/** Os elementos com geometria, fora dos escolhidos, cuja caixa fica a
 *  menos de `raio` da caixa de algum escolhido. */
async function vizinhosDe(
  model: NonNullable<ReturnType<typeof viewerApi.getModel>>,
  escolhidos: number[],
  raio: number,
): Promise<number[]> {
  const todos = await model.getItemsIdsWithGeometry()
  const sao = new Set(escolhidos)
  const outros = todos.filter((id) => !sao.has(id))
  if (outros.length === 0) return []
  const util = (b: THREE.Box3 | undefined): b is THREE.Box3 =>
    Boolean(b) && !b!.isEmpty() && Number.isFinite(b!.min.x) && Number.isFinite(b!.max.x)
  const caixasEsc = (await model.getBoxes(escolhidos)).filter(util).map((b) => b.clone().expandByScalar(raio))
  const caixasOut = await model.getBoxes(outros)
  const out: number[] = []
  for (let i = 0; i < outros.length; i += 1) {
    const b = caixasOut[i]
    if (!util(b)) continue
    for (const c of caixasEsc) {
      if (c.intersectsBox(b)) {
        out.push(outros[i])
        break
      }
    }
  }
  return out
}

/** Cedo o controlo ao browser, para a barra de progresso andar e o
 *  Cancelar responder. */
const ceder = () => new Promise<void>((r) => setTimeout(r, 0))

const LOTE_DE_PONTOS = 200_000
const PONTOS_DO_EIXO = 2000

export async function comparar(
  cloudId: string,
  modelId: string,
  localIds: number[],
  opcoes: OpcoesDaComparacao,
  aoProgredir: (p: ProgressoDaComparacao) => void = () => {},
  sinal?: AbortSignal,
  classePorId?: Map<number, string>,
): Promise<ResultadoDaComparacao> {
  const nuvem = pointCloudApi.list().find((e) => e.id === cloudId)
  const model = viewerApi.getModel(modelId)
  if (!nuvem || !model) throw new Error('A nuvem ou o modelo já não estão abertos.')
  if (localIds.length === 0) throw new Error('Nenhum elemento corresponde ao que se pediu.')
  const abortar = () => {
    if (sinal?.aborted) throw new DOMException('Cancelado', 'AbortError')
  }

  // A origem comum é a colocação do modelo. Num IFC georreferenciado são
  // centenas de quilómetros, e sem isto os milímetros perdiam-se no
  // Float32 dos triângulos.
  model.object.updateMatrixWorld(true)
  const mw = model.object.matrixWorld.elements
  const origem: [number, number, number] = [mw[12], mw[13], mw[14]]

  aoProgredir({ fase: 'geometria', feito: 0, total: localIds.length })
  const tri = await collectWorldTriangles(model as unknown as WorldTrianglesSource, {
    ids: localIds,
    origin: origem,
  })
  abortar()
  const itens = tri.items.filter((i) => i.indices.length >= 3)
  const escolhidos: ElementoAComparar[] = itens.map((i) => ({
    chave: String(i.localId),
    positions: i.positions,
    indices: i.indices,
  }))
  if (escolhidos.length === 0) throw new Error('Os elementos escolhidos não têm geometria que se possa medir.')

  // Os vizinhos entram na medição como concorrentes, e não no relatório.
  // Sem eles, os pontos de um pilar de betão encostado a um pilar de aço
  // eram dados ao de aço, a 5 cm, e o de aço saía com desvio sem ter
  // mexido. Foi o que o primeiro ensaio mostrou: 7 pilares "com desvio"
  // numa nuvem em que só dois tinham sido mexidos. Assim, um ponto é do
  // elemento modelado que lhe fica mais perto, escolhido ou não, e só
  // conta se esse for um dos escolhidos.
  aoProgredir({ fase: 'geometria', feito: 1, total: 2 })
  const vizinhos = await vizinhosDe(model, localIds, opcoes.raio)
  abortar()
  const triViz = vizinhos.length
    ? await collectWorldTriangles(model as unknown as WorldTrianglesSource, { ids: vizinhos, origin: origem })
    : null
  abortar()
  const concorrentes: ElementoAComparar[] = (triViz?.items ?? [])
    .filter((i) => i.indices.length >= 3)
    .map((i) => ({ chave: `vizinho:${i.localId}`, positions: i.positions, indices: i.indices }))
  const nEscolhidos = escolhidos.length
  const elementos = [...escolhidos, ...concorrentes]
  const grelha = construirGrelha(elementos, opcoes.raio)
  if (!grelha) throw new Error('A geometria à volta dos elementos escolhidos é grande demais para medir.')

  // Os cortes activos, se se pediu para os respeitar.
  const planos: PlanoRelativo[] = []
  if (opcoes.respeitarCortes) {
    const renderer = viewerApi.getWorld()?.renderer?.three
    for (const p of renderer?.clippingPlanes ?? []) {
      planos.push(planoRelativo([p.normal.x, p.normal.y, p.normal.z], p.constant, origem))
    }
  }

  // Pontos da nuvem: local -> mundo -> origem comum, tudo em float64.
  nuvem.object.updateMatrixWorld(true)
  const m = nuvem.object.matrixWorld.elements
  const tx = m[12] - origem[0]
  const ty = m[13] - origem[1]
  const tz = m[14] - origem[2]
  const geometria = nuvem.object.geometry
  const pos = geometria.getAttribute('position') as THREE.BufferAttribute
  const arr = pos.array as Float32Array
  const n = pos.count
  const cores = new Uint8Array(n * 3)
  const estat: Estatistica[] = escolhidos.map(() => novaEstatistica())
  const achado = { elemento: -1, distancia: Infinity }
  let noCorte = 0
  let atribuidos = 0
  let deVizinhos = 0

  for (let inicio = 0; inicio < n; inicio += LOTE_DE_PONTOS) {
    abortar()
    aoProgredir({ fase: 'pontos', feito: inicio, total: n })
    const fim = Math.min(n, inicio + LOTE_DE_PONTOS)
    for (let i = inicio; i < fim; i += 1) {
      const lx = arr[i * 3], ly = arr[i * 3 + 1], lz = arr[i * 3 + 2]
      const x = m[0] * lx + m[4] * ly + m[8] * lz + tx
      const y = m[1] * lx + m[5] * ly + m[9] * lz + ty
      const z = m[2] * lx + m[6] * ly + m[10] * lz + tz
      if (planos.length > 0 && !dentroDosPlanos(planos, x, y, z)) {
        // Fora do corte: nem conta, nem se pinta de desvio.
        corDoDesvio(-1, opcoes.tolerancia, cores, i * 3)
        continue
      }
      noCorte += 1
      maisProximo(grelha, x, y, z, achado)
      if (achado.elemento < 0 || achado.elemento >= nEscolhidos) {
        if (achado.elemento >= nEscolhidos) deVizinhos += 1
        corDoDesvio(-1, opcoes.tolerancia, cores, i * 3)
        continue
      }
      atribuidos += 1
      acumular(estat[achado.elemento], x, y, z, achado.distancia, opcoes.tolerancia)
      corDoDesvio(achado.distancia, opcoes.tolerancia, cores, i * 3)
    }
    await ceder()
  }

  // O eixo, para as peças com pontos que cheguem.
  const eixos: (Eixo | SemEixo | null)[] = escolhidos.map(() => null)
  for (let e = 0; e < nEscolhidos; e += 1) {
    if (e % 20 === 0) {
      abortar()
      aoProgredir({ fase: 'eixos', feito: e, total: nEscolhidos })
      await ceder()
    }
    const s = estat[e]
    if (s.pontos === 0) continue
    const el = e
    // A amostra já é aleatória (reservatório): os primeiros 2000 pontos
    // são uma amostra ao acaso, e chegam para o ajuste. Com os 4000 o
    // ajuste levava o dobro sem ganho que se veja.
    eixos[e] = ajustarEixo(escolhidos[e].positions, s.amostra.slice(0, PONTOS_DO_EIXO * 3), (x, y, z, pq, pn) =>
      pontoMaisProximoDoElemento(grelha, el, x, y, z, pq, pn) >= 0,
    )
  }

  const base = relatorio(escolhidos, estat, opcoes.tolerancia)
  const ids = base.map((l) => Number(l.chave))
  const nomes = await namesFor(modelId, ids)
  const classes = classePorId ?? (await classesDe(modelId, ids))
  const linhas: LinhaComNome[] = base.map((l, i) => ({
    ...l,
    modelId,
    localId: ids[i],
    nome: nomes[i],
    classe: classes.get(ids[i]) ?? '',
    eixo: eixos[l.indice],
  }))

  const anterior = resultados.get(cloudId)
  const coresDoLevantamento =
    anterior && anterior.geometria === geometria
      ? anterior.coresDoLevantamento
      : ((geometria.getAttribute('color') as THREE.BufferAttribute | undefined) ?? null)
  const resultado: ResultadoDaComparacao = {
    cloudId,
    modelId,
    opcoes,
    linhas,
    pontosVistos: n,
    pontosNoCorte: noCorte,
    pontosAtribuidos: atribuidos,
    pontosDeVizinhos: deVizinhos,
    vizinhos: concorrentes.length,
    elementosSemGeometria: localIds.length - nEscolhidos,
    truncado: tri.truncated || Boolean(triViz?.truncated),
    coresDoLevantamento,
    coresDoDesvio: new THREE.BufferAttribute(cores, 3, true),
    geometria,
    modo: 'desvio',
  }
  resultados.set(cloudId, resultado)
  aplicarCores(resultado)
  avisar()
  return resultado
}

function aplicarCores(r: ResultadoDaComparacao): void {
  const nuvem = pointCloudApi.list().find((e) => e.id === r.cloudId)
  if (!nuvem || nuvem.object.geometry !== r.geometria) return
  const material = nuvem.object.material as THREE.PointsMaterial
  if (r.modo === 'desvio') {
    r.geometria.setAttribute('color', r.coresDoDesvio)
    material.vertexColors = true
    material.color.set(0xffffff)
  } else if (r.coresDoLevantamento) {
    r.geometria.setAttribute('color', r.coresDoLevantamento)
    material.vertexColors = true
  } else {
    r.geometria.deleteAttribute('color')
    material.vertexColors = false
  }
  material.needsUpdate = true
  viewerApi._invalidate()
}

export const comparacao = {
  subscribe(o: () => void): () => void {
    ouvintes.add(o)
    return () => ouvintes.delete(o)
  },
  versao(): number {
    return versao
  },
  de(cloudId: string): ResultadoDaComparacao | null {
    return resultados.get(cloudId) ?? null
  },
  /** Trocar entre as cores do levantamento e as do desvio, sem
   *  comparar outra vez. */
  mostrar(cloudId: string, modo: 'levantamento' | 'desvio'): void {
    const r = resultados.get(cloudId)
    if (!r || r.modo === modo) return
    r.modo = modo
    aplicarCores(r)
    avisar()
  },
  /** Largar o resultado e devolver as cores do levantamento. */
  limpar(cloudId: string): void {
    const r = resultados.get(cloudId)
    if (!r) return
    r.modo = 'levantamento'
    aplicarCores(r)
    resultados.delete(cloudId)
    avisar()
  },
}
